import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Alert, Badge, Button, Collapse, Group, Paper, Stack, Text } from "@mantine/core"
import { IconChevronDown, IconChevronUp, IconKey } from "@tabler/icons-react"
import { useCallback, useState } from "react"
import { ADAPTER_LABELS, type TCircuitState } from "../constants"
import { FallbackChainSection } from "../FallbackChainSection"
import { TranscriptionProviderCard } from "../TranscriptionProviderCard"
import type { IProps } from "./interfaces"

// Providers with a settable API key. `local` is on-device — no key, no row body.
// Azure and MAI additionally need a region alongside the key. azure-llm has a
// settable key of its own but reuses the MAI region (and the MAI key when empty).
const KEYED_PROVIDERS = new Set(["deepgram", "speechmatics", "soniox", "assemblyai", "azure", "mai", "azure-llm"])
const REGION_PROVIDERS = new Set(["azure", "mai"])

/** Plain-language circuit-breaker health chip (mirrors FallbackChainSection). */
function healthChip(state: TCircuitState | undefined, i18n: I18n): React.JSX.Element {
  if (state === undefined) {
    return (
      <Text size="xs" c="dimmed">
        {i18n._(msg`No data yet`)}
      </Text>
    )
  }
  if (state === "open") {
    return (
      <Badge variant="light" color="danger" size="sm">
        {i18n._(msg`Failing`)}
      </Badge>
    )
  }
  if (state === "half-open") {
    return (
      <Badge variant="light" color="warning" size="sm">
        {i18n._(msg`Cooling down`)}
      </Badge>
    )
  }
  return (
    <Badge variant="dot" color="success" size="sm">
      {i18n._(msg`Healthy`)}
    </Badge>
  )
}

/**
 * Merged transcription-providers card: the ordered batch chain as the primary
 * rows (rank, health, key status, and an expandable key entry per provider),
 * plus a compact live-order secondary section. Replaces the old separate
 * "Provider order" panel and "Transcription API Keys" list — one card per
 * concept. Purely presentational; ordering + key persistence flow through the
 * parent SettingsView.
 */
export const TranscriptionProvidersCard = ({
  batchOrder,
  streamingOrder,
  savingFallback,
  health,
  resolvedKeys,
  onMoveBatch,
  onSaveBatch,
  onMoveStreaming,
  onSaveStreaming,
  settings,
  configuredSecrets,
  saving,
  isAdminOnly,
  onApplyOrderToAllTenants,
  applyingOrderToAllTenants,
  company,
  updateSetting,
  save,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const canEditKeys = isAdminOnly || !!company

  const toggle = useCallback((name: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const keyStatusChip = (name: string): React.JSX.Element | null => {
    if (!KEYED_PROVIDERS.has(name)) {
      return (
        <Badge variant="light" color="info" size="sm">
          <Trans>On-device</Trans>
        </Badge>
      )
    }
    // Resolution truth beats assumption: when the backend says NO key resolves
    // for this provider (org BYOK → platform → env — the same check the chain
    // resolver uses), the runtime will silently skip it. Warn exactly that,
    // instead of the optimistic "Platform key" chip.
    if (resolvedKeys?.[name] === false) {
      return (
        <Badge variant="light" color="warning" size="sm">
          <Trans>No key — will be skipped</Trans>
        </Badge>
      )
    }
    if (isAdminOnly) {
      if (configuredSecrets.has(`platform_${name}_key`)) {
        return (
          <Badge variant="light" color="success" size="sm">
            <Trans>Key set</Trans>
          </Badge>
        )
      }
      // No platform settings row, but resolution says a key IS usable — it
      // comes from the server environment. Say that, instead of the old
      // false-negative "Not set" (prod ships every platform key via env).
      if (resolvedKeys?.[name] === true) {
        return (
          <Badge variant="light" color="success" size="sm">
            <Trans>Server key</Trans>
          </Badge>
        )
      }
      // Resolution truth unknown (still loading / request failed): claim
      // nothing rather than assert "Not set".
      if (resolvedKeys === undefined) return null
      return (
        <Badge variant="outline" color="gray" size="sm">
          <Trans>Not set</Trans>
        </Badge>
      )
    }
    return configuredSecrets.has(`org_${name}_key`) ? (
      <Badge variant="light" color="success" size="sm">
        <Trans>Your key</Trans>
      </Badge>
    ) : (
      <Badge variant="light" color="info" size="sm">
        <Trans>Platform key</Trans>
      </Badge>
    )
  }

  return (
    <Stack gap="md">
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>
            These providers turn your audio into text. Order sets priority — position 1 is your primary; if it fails 3
            times in a row the next takes over automatically and the failed one rests for 5 minutes. Expand a provider
            to use your own key (BYOK) instead of the platform's. Health is shown as seen by the server that answered
            this request.
          </Trans>
        </Text>
      </Alert>

      <Stack gap="xs">
        {batchOrder.map((name, index) => {
          const label = ADAPTER_LABELS[name] ?? name
          const canExpand = KEYED_PROVIDERS.has(name) && canEditKeys
          const isOpen = expanded.has(name)
          const prefix = isAdminOnly ? "platform" : "org"
          const keyName = `${prefix}_${name}_key`
          const regionName = REGION_PROVIDERS.has(name) ? `${prefix}_${name}_region` : undefined
          return (
            <Paper withBorder p="sm" key={name}>
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Badge variant="filled" color="brand" size="sm">
                    {index + 1}
                  </Badge>
                  <Text size="sm" fw={500}>
                    {label}
                  </Text>
                  {index === 0 && (
                    <Badge variant="light" color="brand" size="sm">
                      <Trans>Primary</Trans>
                    </Badge>
                  )}
                  {health !== undefined && healthChip(health[name], i18n)}
                  {keyStatusChip(name)}
                </Group>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={index === 0}
                    onClick={() => onMoveBatch(index, index - 1)}
                    aria-label={i18n._(msg`Move ${label} up`)}
                  >
                    <IconChevronUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={index === batchOrder.length - 1}
                    onClick={() => onMoveBatch(index, index + 1)}
                    aria-label={i18n._(msg`Move ${label} down`)}
                  >
                    <IconChevronDown size={16} />
                  </ActionIcon>
                  {canExpand && (
                    <ActionIcon
                      variant={isOpen ? "light" : "subtle"}
                      color="brand"
                      size="sm"
                      onClick={() => toggle(name)}
                      aria-label={i18n._(msg`Manage ${label} API key`)}
                      aria-expanded={isOpen}
                    >
                      <IconKey size={16} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
              {canExpand && (
                <Collapse in={isOpen}>
                  <Paper bg="var(--mantine-color-default-hover)" p="sm" mt="sm" radius="sm">
                    <TranscriptionProviderCard
                      bare
                      name={name}
                      label={label}
                      settingKey={keyName}
                      currentKey={settings[keyName] ?? ""}
                      configured={configuredSecrets.has(keyName)}
                      onKeyChange={(v) => updateSetting(keyName, v)}
                      onSave={() => save(regionName ? [keyName, regionName] : [keyName])}
                      saving={saving}
                      company={company}
                      regionKey={regionName}
                      currentRegion={regionName ? (settings[regionName] ?? "") : undefined}
                      onRegionChange={regionName ? (v) => updateSetting(regionName, v) : undefined}
                    />
                    {name === "azure-llm" && (
                      <Text size="xs" c="dimmed" mt="xs">
                        <Trans>Uses the MAI-Transcribe-2 key and region when left empty.</Trans>
                      </Text>
                    )}
                  </Paper>
                </Collapse>
              )}
            </Paper>
          )
        })}
        <Group>
          <Button size="sm" onClick={onSaveBatch} loading={savingFallback}>
            <Trans>Save order</Trans>
          </Button>
        </Group>
      </Stack>

      <FallbackChainSection
        title={i18n._(msg`Live recording order`)}
        description={i18n._(
          msg`Priority while recording live. Only streaming-capable providers appear here; keys are shared with the list above.`,
        )}
        order={streamingOrder}
        saving={savingFallback}
        health={health}
        resolvedKeys={resolvedKeys}
        onMove={onMoveStreaming}
        onSave={onSaveStreaming}
      />

      {isAdminOnly && onApplyOrderToAllTenants && (
        <Paper withBorder p="sm">
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text size="sm" fw={500}>
                <Trans>Apply this order to every workspace</Trans>
              </Text>
              <Text size="xs" c="dimmed">
                <Trans>
                  A workspace that saved its own provider order ignores the platform default. This removes those
                  overrides (both lists above), so every workspace follows the platform order — including future
                  changes. Workspace API keys are not touched.
                </Trans>
              </Text>
            </Stack>
            <Button
              size="sm"
              variant="light"
              color="warning"
              loading={applyingOrderToAllTenants}
              onClick={onApplyOrderToAllTenants}
            >
              <Trans>Apply to all</Trans>
            </Button>
          </Group>
        </Paper>
      )}
    </Stack>
  )
}
